import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { CLIENT_REPORTING_BENCHMARKS } from './clientReportingBenchmarkConfig';
import {
  clientReportingRangeToIso,
  computeBenchmarkMetricsFromApi,
  computeYouMetricsFromSeries,
  computeYouSummary,
  EMPTY_BENCHMARK_METRICS,
  portfolioBaseValue,
} from './clientReportingBenchmarkMetrics';

export default function useClientReportingBenchmarks({
  seriesRaw = {},
  seriesOrder = [],
  effectiveRange = 'All',
  primaryBroker = '',
  realizedProfit = null,
  netContributed = null,
}) {
  const [selectedBenchmarkIds, setSelectedBenchmarkIds] = useState(() => new Set());
  const [benchmarkDataById, setBenchmarkDataById] = useState({});
  const [benchmarkMetricsById, setBenchmarkMetricsById] = useState({});
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState('');

  const portfolioSeries = useMemo(() => {
    const primaryName = seriesOrder[0];
    if (!primaryName) return [];
    return seriesRaw?.[primaryName] || [];
  }, [seriesOrder, seriesRaw]);

  const youMetrics = useMemo(
    () => computeYouMetricsFromSeries(portfolioSeries, {
      broker: primaryBroker,
      realizedProfit,
      netContributed,
    }),
    [portfolioSeries, primaryBroker, realizedProfit, netContributed],
  );

  const youSummary = useMemo(
    () => computeYouSummary(portfolioSeries, youMetrics),
    [portfolioSeries, youMetrics],
  );

  const baseValue = useMemo(
    () => portfolioBaseValue(portfolioSeries),
    [portfolioSeries],
  );

  // Pass the client's series so "All" resolves to their inception date rather
  // than the benchmark vendor's full history.
  const rangeIso = useMemo(
    () => clientReportingRangeToIso(effectiveRange, portfolioSeries),
    [effectiveRange, portfolioSeries],
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function loadBenchmarks() {
      setLoading(true);
      setWarning('');

      try {
        const settled = await Promise.allSettled(
          CLIENT_REPORTING_BENCHMARKS.map(async (benchmark) => {
            const res = await fetch(
              API_ENDPOINTS.benchmarkSeries({
                benchmark: benchmark.id,
                startDate: rangeIso.startDate,
                endDate: rangeIso.endDate,
                baseValue,
              }),
              {
                headers: getBearerAuthHeaders(),
                signal: controller.signal,
              },
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data.success === false) {
              throw new Error(data.error || `Failed to load ${benchmark.id}`);
            }
            return {
              id: benchmark.id,
              data,
              metrics: computeBenchmarkMetricsFromApi(data),
            };
          }),
        );

        if (cancelled) return;

        const nextData = {};
        const nextMetrics = {};
        const failed = [];

        settled.forEach((result, index) => {
          const id = CLIENT_REPORTING_BENCHMARKS[index].id;
          if (result.status === 'fulfilled') {
            nextData[id] = result.value.data;
            nextMetrics[id] = result.value.metrics;
          } else if (result.reason?.name !== 'AbortError') {
            failed.push(id);
            nextMetrics[id] = { ...EMPTY_BENCHMARK_METRICS };
          }
        });

        setBenchmarkDataById(nextData);
        setBenchmarkMetricsById(nextMetrics);
        if (failed.length) {
          setWarning(`Some benchmark data unavailable: ${failed.join(', ')}`);
        }
      } catch (err) {
        if (cancelled || err.name === 'AbortError') return;
        console.error('[client-reporting-benchmarks]', err);
        setBenchmarkDataById({});
        setBenchmarkMetricsById({});
        setWarning(err.message || 'Benchmark data unavailable.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadBenchmarks();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [baseValue, rangeIso.endDate, rangeIso.startDate]);

  const toggleBenchmark = useCallback((benchmarkId) => {
    setSelectedBenchmarkIds((prev) => {
      const next = new Set(prev);
      if (next.has(benchmarkId)) next.delete(benchmarkId);
      else next.add(benchmarkId);
      return next;
    });
  }, []);

  return {
    selectedBenchmarkIds,
    toggleBenchmark,
    benchmarkDataById,
    benchmarkMetricsById,
    youMetrics,
    youSummary,
    baseValue,
    loading,
    warning,
  };
}
